import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { FileText, TrendingUp, Newspaper, CheckCircle, Clock, XCircle } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { quotesAPI, revenueAPI } from '../../services/api';

export const Dashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ totalRevenue: 0, acceptedQuotesCount: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesData, revenueData] = await Promise.all([
        quotesAPI.getAll(),
        revenueAPI.getStats()
      ]);
      setQuotes(quotesData);
      setStats(revenueData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const pendingQuotes = quotes.filter(q => q.status === 'pending').length;
  const acceptedQuotes = quotes.filter(q => q.status === 'accepted').length;
  const rejectedQuotes = quotes.filter(q => q.status === 'rejected').length;

  const dashboardCards = [
    {
      title: 'Devis en attente',
      value: pendingQuotes,
      icon: Clock,
      color: 'orange',
      link: '/admin/quotes?status=pending'
    },
    {
      title: 'Devis acceptés',
      value: acceptedQuotes,
      icon: CheckCircle,
      color: 'green',
      link: '/admin/quotes?status=accepted'
    },
    {
      title: 'Devis refusés',
      value: rejectedQuotes,
      icon: XCircle,
      color: 'red',
      link: '/admin/quotes?status=rejected'
    },
    {
      title: 'CA Total',
      value: `${(stats.totalRevenue / 1000000).toFixed(1)}M FCFA`,
      icon: TrendingUp,
      color: 'blue',
      link: '/admin/revenue'
    }
  ];

  const quickActions = [
    {
      title: 'Gérer les devis',
      description: 'Créer, modifier ou supprimer des devis',
      icon: FileText,
      link: '/admin/quotes',
      color: 'green'
    },
    {
      title: 'Voir le CA',
      description: 'Analyser le chiffre d\'affaires et exporter',
      icon: TrendingUp,
      link: '/admin/revenue',
      color: 'orange'
    },
    {
      title: 'Gérer le blog',
      description: 'Publier des actualités et newsletters',
      icon: Newspaper,
      link: '/admin/blog',
      color: 'blue'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-600',
      orange: 'bg-orange-100 text-orange-600',
      red: 'bg-red-100 text-red-600',
      blue: 'bg-blue-100 text-blue-600'
    };
    return colors[color] || colors.green;
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600 mb-8">Vue d'ensemble de votre activité</p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Link key={index} to={card.link}>
                <Card className="hover:shadow-lg transition-shadow bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{card.title}</p>
                        <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                      </div>
                      <div className={`p-3 rounded-full ${getColorClasses(card.color)}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Actions rapides</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Link key={index} to={action.link}>
                  <Card className="hover:shadow-lg transition-all hover:scale-105 bg-white">
                    <CardContent className="p-6">
                      <div className={`w-fit p-3 rounded-lg mb-4 ${getColorClasses(action.color)}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{action.title}</h3>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Quotes */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Devis récents</h2>
            <Link to="/admin/quotes" className="text-green-600 hover:text-green-700 font-medium">
              Voir tous →
            </Link>
          </div>
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="space-y-4">
                {quotes.slice(0, 5).map((quote) => (
                  <div key={quote.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{quote.clientName}</p>
                      <p className="text-sm text-gray-600">{quote.company} - {quote.service}</p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="font-semibold text-gray-900">{(quote.amount / 1000000).toFixed(2)}M FCFA</p>
                      <p className="text-xs text-gray-500">{new Date(quote.date).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div>
                      {quote.status === 'accepted' && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          Accepté
                        </span>
                      )}
                      {quote.status === 'pending' && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                          En attente
                        </span>
                      )}
                      {quote.status === 'rejected' && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          Refusé
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};